import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '~/store'
import { canAccessAdminLevel, parseAdminLevel } from '~/utils/admin-level'

export interface AdminMenuItem {
  icon?: string
  name: string
  title: string
  to: string
  minLevel?: number
}

export interface AdminMenuGroup {
  id: string
  label: string
  items: AdminMenuItem[]
}

interface MenuItemDef {
  icon?: string
  name: string
  titleKey: string
  to: string
  minLevel?: number
}

interface MenuGroupDef {
  id: string
  sectionKey: string
  items: MenuItemDef[]
}

/**
 * Admin nav + route ACL.
 * Prefer `getAdminRequiredNivelByPath()` over bare `layout.php` links — legacy often
 * showed a menu entry that the page ACL then denied. Higher `minLevel` = more privileged.
 * Items without `minLevel` stay open to any logged-in admin (legacy ungated paths).
 *
 * Section order follows staff workflow:
 * staff tools → community → game entities → commerce → content → analytics → infra.
 */
const MENU_GROUP_DEFS: MenuGroupDef[] = [
  {
    id: 'general',
    items: [
      { icon: 'i-tabler-home', name: 'dashboard', titleKey: 'common.home', to: '/dashboard' },
      { icon: 'i-tabler-headset', name: 'helpdesk', titleKey: 'HELPDESK', to: '/helpdesk' },
    ],
    sectionKey: 'menu.sections.general',
  },
  {
    id: 'admin',
    items: [
      { icon: 'i-tabler-user-shield', minLevel: 10, name: 'administrators', titleKey: 'ADMINISTRADORES', to: '/administrators' },
      { icon: 'i-tabler-key', name: 'users-change-password', titleKey: 'TROCAR_SENHA', to: '/users/change-password' },
    ],
    sectionKey: 'menu.sections.admin',
  },
  {
    id: 'users',
    items: [
      { icon: 'i-tabler-users', minLevel: 10, name: 'users', titleKey: 'USUARIOS', to: '/users' },
      { icon: 'i-tabler-history', minLevel: 5, name: 'users-history', titleKey: 'HISTORICO_USUARIOS', to: '/users/history' },
      { icon: 'i-tabler-user-search', minLevel: 5, name: 'users-registrations-info', titleKey: 'CADASTROS', to: '/users/registrations-info' },
      { icon: 'i-tabler-clock', minLevel: 5, name: 'users-last-ip-accesses', titleKey: 'ULTIMOS_ACESSOS', to: '/users/last-ip-accesses' },
    ],
    sectionKey: 'menu.sections.users',
  },
  {
    id: 'moderation',
    items: [
      { icon: 'i-tabler-file-text', minLevel: 10, name: 'moderation-block', titleKey: 'MODERACAO_USUARIOS_REPORTADOS', to: '/moderation/block' },
      { icon: 'i-tabler-list-numbers', minLevel: 10, name: 'moderation-decision-list', titleKey: 'MODERACAO_DECISAO_LISTA', to: '/moderation/block/decision-list' },
      { icon: 'i-tabler-flag', name: 'moderation-reported-teams', titleKey: 'MODERACAO_EQUIPES_REPORTADAS', to: '/moderation/reported-teams' },
    ],
    sectionKey: 'menu.sections.moderation',
  },
  {
    id: 'teams',
    items: [
      { icon: 'i-tabler-arrows-exchange', name: 'transfers', titleKey: 'TRANSFERENCIAS', to: '/transfers' },
      { icon: 'i-tabler-robot', minLevel: 5, name: 'teams-bots', titleKey: 'EQUIPES_BOT', to: '/teams/bots' },
      { icon: 'i-tabler-flag', minLevel: 5, name: 'teams-flags', titleKey: 'BANDEIRAS', to: '/teams/flags' },
    ],
    sectionKey: 'menu.sections.teams',
  },
  {
    id: 'players',
    items: [
      { icon: 'i-tabler-user-plus', minLevel: 10, name: 'create-player', titleKey: 'CRIAR_JOGADOR', to: '/create-player' },
      { icon: 'i-tabler-users-group', name: 'players-group', titleKey: 'JOGADORES_GROUP', to: '/players/group' },
      { icon: 'i-tabler-forms', name: 'players-attributes', titleKey: 'ATRIBUTOS_JOGADORES', to: '/players/attributes' },
      { icon: 'i-tabler-photo', name: 'players-update-request', titleKey: 'REQUISICAO_UPDATE_IMAGEM', to: '/players/update-request' },
      { icon: 'i-tabler-tools', name: 'players-search-fix', titleKey: 'JOGADORES_SEARCH_FIX', to: '/players/search-fix' },
    ],
    sectionKey: 'menu.sections.players',
  },
  {
    id: 'auction',
    items: [
      { icon: 'i-tabler-users', name: 'auction-players', titleKey: 'JOGADORES_LEILAO', to: '/auction/players' },
      { icon: 'i-tabler-plus', name: 'auction-create', titleKey: 'CRIAR_JOGADOR_LEILAO', to: '/auction/create' },
    ],
    sectionKey: 'menu.sections.auction',
  },
  {
    id: 'championships',
    items: [
      { icon: 'i-tabler-trophy', minLevel: 5, name: 'private-championships', titleKey: 'CAMPEONATOS_PRIVADOS', to: '/private-championships' },
      { icon: 'i-tabler-trash', minLevel: 5, name: 'delete-championship', titleKey: 'DELETAR_CAMPEONATOS', to: '/delete-championship' },
      { icon: 'i-tabler-lock-open', minLevel: 5, name: 'unlock-championships', titleKey: 'DESTRAVAR_CAMPEONATOS', to: '/unlock-championships' },
      { icon: 'i-tabler-trophy', minLevel: 5, name: 'mlf', titleKey: 'LISTA_MLF', to: '/mlf' },
    ],
    sectionKey: 'menu.sections.championships',
  },
  {
    id: 'gameOps',
    items: [
      { icon: 'i-tabler-star', name: 'totw', titleKey: 'TOTW', to: '/totw' },
      { icon: 'i-tabler-archive', minLevel: 5, name: 'coach-pass', titleKey: 'PASSE_TREINADOR', to: '/coach-pass' },
      { icon: 'i-tabler-flag', minLevel: 5, name: 'missions', titleKey: 'MISSOES', to: '/missions' },
      { icon: 'i-tabler-users-group', minLevel: 5, name: 'sponsors', titleKey: 'PATROCINADORES', to: '/sponsors' },
      { icon: 'i-tabler-trophy', name: 'weekly-ranking', titleKey: 'RANKING_SEMANAL', to: '/weekly-ranking' },
      { icon: 'i-tabler-trophy', name: 'clan-ranking', titleKey: 'RANKING_CLAN_SEMANAL', to: '/clan-ranking' },
    ],
    sectionKey: 'menu.sections.gameOps',
  },
  {
    id: 'shop',
    items: [
      { icon: 'i-tabler-package', minLevel: 5, name: 'packages', titleKey: 'PACOTES', to: '/packages' },
      { icon: 'i-tabler-shopping-cart', minLevel: 5, name: 'shop', titleKey: 'LOJA', to: '/shop' },
      { icon: 'i-tabler-crown', minLevel: 5, name: 'vip-shop', titleKey: 'LOJA_VIP', to: '/vip-shop' },
      { icon: 'i-tabler-shopping-bag', minLevel: 5, name: 'accessories', titleKey: 'ACESSORIOS', to: '/accessories' },
    ],
    sectionKey: 'menu.sections.shop',
  },
  {
    id: 'promotions',
    items: [
      { icon: 'i-tabler-users', minLevel: 5, name: 'promotions', titleKey: 'PROMOCOES', to: '/promotions' },
      { icon: 'i-tabler-calendar-event', minLevel: 5, name: 'promotion-schedules', titleKey: 'PROMOCOES_AGENDADAS', to: '/promotions/schedules' },
      { icon: 'i-tabler-discount', minLevel: 5, name: 'fm-promotion', titleKey: 'PROMOCAO_FM', to: '/fm-promotion' },
      { icon: 'i-tabler-gift', minLevel: 5, name: 'redeem-promotions', titleKey: 'RESGATAR_PROMOCAO', to: '/redeem-promotions' },
    ],
    sectionKey: 'menu.sections.promotions',
  },
  {
    id: 'content',
    items: [
      { icon: 'i-tabler-news', minLevel: 5, name: 'news', titleKey: 'NOTICIAS', to: '/news' },
      { icon: 'i-tabler-bulb', minLevel: 5, name: 'tips', titleKey: 'DICAS', to: '/tips' },
      { icon: 'i-tabler-broadcast', minLevel: 5, name: 'streaming', titleKey: 'STREAMING', to: '/streaming' },
      { icon: 'i-tabler-database', minLevel: 5, name: 'embedding', titleKey: 'CONTEXTOS', to: '/embedding' },
    ],
    sectionKey: 'menu.sections.content',
  },
  {
    id: 'reports',
    items: [
      { icon: 'i-tabler-list', minLevel: 5, name: 'reports', titleKey: 'RELATORIO_JOGADOR', to: '/reports' },
      { icon: 'i-tabler-coin', minLevel: 5, name: 'reports-fmcoin', titleKey: 'RELATORIO_COIN_EQUIPE', to: '/reports/fmcoin' },
      { icon: 'i-tabler-package', minLevel: 5, name: 'reports-package', titleKey: 'RELATORIO_PACOTES', to: '/reports/package' },
      { icon: 'i-tabler-users-group', minLevel: 5, name: 'reports-base-category', titleKey: 'reports.baseCategory.title', to: '/reports/base-category' },
      { icon: 'i-tabler-barbell', minLevel: 5, name: 'reports-training', titleKey: 'RELATORIO_TREINAMENTO', to: '/reports/training' },
      { icon: 'i-tabler-chart-line', minLevel: 10, name: 'charts', titleKey: 'GRAFICO', to: '/charts' },
      { icon: 'i-tabler-receipt', minLevel: 10, name: 'purchase-log', titleKey: 'ADMIN_COMPRA_LOG_TITLE', to: '/purchase-log' },
      { icon: 'i-tabler-list-details', minLevel: 10, name: 'orders', titleKey: 'PEDIDOS', to: '/orders' },
    ],
    sectionKey: 'menu.sections.reports',
  },
  {
    id: 'gameReports',
    items: [
      { icon: 'i-tabler-circle-check', minLevel: 10, name: 'confirmar-pagseguro', titleKey: 'gameReports.menu.confirmPagseguro', to: '/confirmar-pagseguro' },
      { icon: 'i-tabler-history', minLevel: 5, name: 'historico-equipes', titleKey: 'gameReports.menu.teamHistory', to: '/historico-equipes' },
      { icon: 'i-tabler-user-plus', minLevel: 5, name: 'ultimos-cadastros', titleKey: 'gameReports.menu.latestRegistrations', to: '/ultimos-cadastros' },
      { icon: 'i-tabler-arrow-back-up', minLevel: 5, name: 'retorno-usuarios', titleKey: 'gameReports.menu.userReturn', to: '/retorno-usuarios' },
      { icon: 'i-tabler-thumb-up', minLevel: 5, name: 'retencao-usuarios', titleKey: 'gameReports.menu.userRetention', to: '/retencao-usuarios' },
      { icon: 'i-tabler-chalkboard', minLevel: 5, name: 'relatorio-tutorial', titleKey: 'gameReports.menu.tutorialReport', to: '/relatorio-tutorial' },
      { icon: 'i-tabler-chart-line', minLevel: 5, name: 'relatorio-online', titleKey: 'gameReports.menu.onlineReport', to: '/relatorio-online' },
      { icon: 'i-tabler-shopping-cart', minLevel: 5, name: 'relatorio-usuario-vendas-sem-login', titleKey: 'gameReports.menu.salesWithoutLogin', to: '/relatorio-usuario-vendas-sem-login' },
    ],
    sectionKey: 'menu.sections.gameReports',
  },
  {
    id: 'platform',
    items: [
      { icon: 'i-tabler-server', minLevel: 10, name: 'ovh', titleKey: 'common.ovh', to: '/ovh' },
      { icon: 'i-tabler-phone', minLevel: 10, name: 'whatsapp', titleKey: 'common.whatsapp', to: '/whatsapp' },
      { icon: 'i-tabler-robot', minLevel: 10, name: 'simulator', titleKey: 'SIMULADORES', to: '/simulator' },
    ],
    sectionKey: 'menu.sections.platform',
  },
]

const MENU_ROUTE_DEFS: MenuItemDef[] = MENU_GROUP_DEFS.flatMap(group => group.items)

/** Routes not listed as top-level menu items but still ACL-gated (legacy path map). */
const EXTRA_ROUTE_MIN_LEVELS: Array<{ minLevel: number, to: string }> = [
  { minLevel: 10, to: '/player' },
  { minLevel: 10, to: '/teams/admin' },
  { minLevel: 10, to: '/moderation/block/decision' },
  { minLevel: 10, to: '/moderation/block/admin-decision' },
]

/** Longest-prefix match against menu routes (e.g. `/orders/information` → `/orders`). */
export function requiredMinLevelForPath(routePath: string): number {
  const path = routePath.split('?')[0] || '/'
  const match = [...MENU_ROUTE_DEFS, ...EXTRA_ROUTE_MIN_LEVELS]
    .filter(item => path === item.to || path.startsWith(`${item.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0]

  return match?.minLevel ?? 0
}

function mapItem(def: MenuItemDef, t: (key: string) => string): AdminMenuItem {
  return {
    icon: def.icon,
    minLevel: def.minLevel,
    name: def.name,
    title: t(def.titleKey),
    to: def.to,
  }
}

export function useMenu() {
  const { t } = useI18n()
  const userStore = useUserStore()

  const adminNivel = computed(() => parseAdminLevel(userStore.user?.level))

  const menuGroups = computed<AdminMenuGroup[]>(() => {
    const nivel = adminNivel.value

    return MENU_GROUP_DEFS.map((group) => {
      const items = group.items
        .filter(item => canAccessAdminLevel(nivel, item.minLevel))
        .map(item => mapItem(item, t))

      if (!items.length)
        return null

      return {
        id: group.id,
        items,
        label: t(group.sectionKey),
      }
    }).filter((group): group is AdminMenuGroup => group !== null)
  })

  const menuItems = computed(() => menuGroups.value.flatMap(group => group.items))

  function getPageTitle(routePath: string) {
    const items = menuItems.value
    const exactTitle = items.find(item => item.to === routePath)?.title
    if (exactTitle)
      return `${exactTitle} | ${import.meta.env.VITE_TITLE}`

    const prefixItem = items
      .filter(item => item.to !== '/')
      .sort((a, b) => b.to.length - a.to.length)
      .find(item => routePath.startsWith(`${item.to}/`))
    if (prefixItem?.title)
      return `${prefixItem.title} | ${import.meta.env.VITE_TITLE}`

    return import.meta.env.VITE_TITLE
  }

  function isMenuItemActive(routePath: string, routeName: string | symbol | null | undefined, item: { name: string, to: string }) {
    const items = menuItems.value
    const bestMatch = items
      .filter(candidate => routePath === candidate.to || routePath.startsWith(`${candidate.to}/`))
      .sort((a, b) => b.to.length - a.to.length)[0]

    if (bestMatch)
      return bestMatch.to === item.to

    return routeName === item.name
  }

  return {
    adminNivel,
    getPageTitle,
    isMenuItemActive,
    menuGroups,
    menuItems,
  }
}
